import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, Subject, timer } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  MerchantService,
  MerchantServicesReportRun,
  MerchantServicesReportRunStatus,
  MerchantServicesReportRunType,
} from '@galaxy/billing';

import { saveAs } from 'file-saver';

import { TranslateService } from '@ngx-translate/core';
import { BuildStatus, ExportRequest } from '../models';

const pollInterval = 2500;

interface State {
  buildStatus?: BuildStatus;
  errorMessage?: string | null;
}

const defaultState: State = {
  buildStatus: BuildStatus.IDLE,
};

@Injectable()
export class ExportService {
  private state$$: BehaviorSubject<State> = new BehaviorSubject(defaultState);
  private state$: Observable<State> = this.state$$.asObservable();

  private cancel$$: Subject<boolean> = new Subject();

  constructor(
    private merchantService: MerchantService,
    private translateService: TranslateService,
  ) {}

  // Export financial report data.
  export(request: ExportRequest): void {
    this.setState({ buildStatus: BuildStatus.BUILDING, errorMessage: null });

    const newReportRun$ = this.merchantService.createReportRun(
      request.merchantID,
      MerchantServicesReportRunType.MERCHANT_SERVICES_REPORT_RUN_TYPE_BALANCE_ITEMIZED,
      request.startInterval,
      request.endInterval,
      request.currency,
    );
    const poller$ = timer(0, pollInterval);
    const reportRun$ = combineLatest([newReportRun$, poller$]).pipe(
      takeUntil(this.cancel$$),
      switchMap(([reportRun, _]) => this.merchantService.getReportRun(reportRun.id)),
      filter((reportRun) => isBuildFinished(reportRun)),
      take(1),
    );

    reportRun$
      .pipe(
        tap((reportRun) => this.setState({ buildStatus: buildStateFromReportRun(reportRun) })),
        switchMap((reportRun) => {
          if (isFailedReportRun(reportRun)) {
            throw new Error(reportRun.error);
          }
          return this.merchantService.getReport(reportRun.id);
        }),
        take(1),
      )
      .subscribe(
        (data) => {
          saveAs(data);
          this.setState({ buildStatus: BuildStatus.IDLE });
        },
        () =>
          this.setState({
            buildStatus: BuildStatus.FAILED,
            errorMessage: this.translateService.instant('BILLING.PAYMENTS_EXPORT.DIALOG.ERROR_MESSAGE'),
          }),
      );
  }

  /**
   * Cancel any in progress report build.
   */
  cancel(): void {
    this.cancel$$.next(true);
    this.setState({ buildStatus: BuildStatus.IDLE, errorMessage: null });
  }

  /**
   * Returns the state of the report build.
   */
  public get buildState$(): Observable<BuildStatus> {
    return this.state$.pipe(map((state) => state.buildStatus));
  }

  /**
   * Returns an observable of the error message.
   */
  public get errorMessage$(): Observable<string | null> {
    return this.state$.pipe(map((state) => state.errorMessage));
  }

  private setState(state: State): void {
    this.state$$.next(Object.assign(this.state$$.getValue(), state));
  }
}

/**
 * Returns true if the report run has finished building.
 * @param reportRun
 */
function isBuildFinished(reportRun: MerchantServicesReportRun): boolean {
  return (
    reportRun.status === MerchantServicesReportRunStatus.MERCHANT_SERVICES_REPORT_RUN_STATUS_SUCCEEDED ||
    reportRun.status === MerchantServicesReportRunStatus.MERCHANT_SERVICES_REPORT_RUN_STATUS_FAILED
  );
}

/**
 * Returns the build state from a completed report run.
 * @param reportRun
 */
function buildStateFromReportRun(reportRun: MerchantServicesReportRun): BuildStatus {
  return reportRun.status === MerchantServicesReportRunStatus.MERCHANT_SERVICES_REPORT_RUN_STATUS_SUCCEEDED
    ? BuildStatus.READ_FOR_DOWNLOAD
    : BuildStatus.FAILED;
}

/**
 * Returns true if the report run failed.
 * @param reportRun
 */
function isFailedReportRun(reportRun: MerchantServicesReportRun): boolean {
  return reportRun.status === MerchantServicesReportRunStatus.MERCHANT_SERVICES_REPORT_RUN_STATUS_FAILED;
}
